import { useState } from "react";
import { brokerageContractsRepository } from "@/repositories/brokerageContracts";
import type { ExtractedBrokerageFields } from "@/types/brokerage";

export interface ExtractionState {
  isLoading: boolean;
  data: ExtractedBrokerageFields | null;
  error: string | null;
}

/**
 * Hook to manage brokerage contract PDF extraction.
 * Calls POST /brokerage-contracts/extract via the brokerageContracts repository
 * (Nest-first — VITE_API_URL required).
 *
 * Salvaged from origin/feat/brokerage-contracts (PR #1344) and re-homed onto
 * the NestJS API built in Inc-1/2. The original `brokerageExtract(file)` call
 * to src/services/api (which did not exist) is replaced with
 * `brokerageContractsRepository.extract(file)`.
 *
 * Usage:
 *   const { extract, state, reset } = useBrokerageExtraction();
 *   await extract(file);
 *   state.data // ExtractedBrokerageFields
 */
export function useBrokerageExtraction() {
  const [state, setState] = useState<ExtractionState>({
    isLoading: false,
    data: null,
    error: null,
  });

  const extract = async (file: File): Promise<ExtractedBrokerageFields | null> => {
    setState({ isLoading: true, data: null, error: null });
    try {
      const result = await brokerageContractsRepository.extract(file);
      setState({ isLoading: false, data: result, error: null });
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : "فشل في استخراج البيانات";
      setState({ isLoading: false, data: null, error: message });
      return null;
    }
  };

  const reset = () => setState({ isLoading: false, data: null, error: null });

  return { extract, state, reset };
}
